// apps/web/src/components/CopyTaskModal.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CopyTaskModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * CopyTaskModal - Modal for copying a task to another list/workspace
 *
 * @param {Object} task - The task to copy
 * @param {Array} workspaces - All workspaces user has access to
 * @param {Function} onCopy - Callback when copy is confirmed: onCopy(targetListId, options)
 * @param {Function} onClose - Callback to close modal
 */
export default function CopyTaskModal({ task, workspaces, onCopy, onClose }) {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [availableLists, setAvailableLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  // Copy options
  const [includeLabels, setIncludeLabels] = useState(true);
  const [includeComments, setIncludeComments] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(false);
  const [includeMediaPanel, setIncludeMediaPanel] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set default workspace and list
  useEffect(() => {
    if (workspaces && workspaces.length > 0 && !selectedWorkspaceId) {
      // Default to first workspace
      setSelectedWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, selectedWorkspaceId]);

  // Fetch lists when workspace changes
  useEffect(() => {
    if (!selectedWorkspaceId) {
      setAvailableLists([]);
      setSelectedListId('');
      return;
    }

    async function fetchLists() {
      setLoadingLists(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${API_URL}/api/v1/workspaces/${selectedWorkspaceId}/lists`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const lists = response.data?.lists || response.data || [];
        setAvailableLists(lists);

        // Default to first list
        if (lists.length > 0) {
          setSelectedListId(lists[0].id);
        } else {
          setSelectedListId('');
        }
      } catch (err) {
        console.error('[CopyTaskModal] Failed to fetch lists:', err);
        setAvailableLists([]);
        setSelectedListId('');
      } finally {
        setLoadingLists(false);
      }
    }

    fetchLists();
  }, [selectedWorkspaceId]);

  const handleCopy = async () => {
    if (!selectedListId) {
      setError('Please select a target list');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const options = {
        includeLabels,
        includeComments,
        includeAttachments,
        includeMediaPanel
      };

      await onCopy(selectedListId, options);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy task');
      setIsLoading(false);
    }
  };

  // Get current workspace and list names for display
  const selectedWorkspace = workspaces.find(w => w.id === selectedWorkspaceId);
  const selectedList = availableLists.find(l => l.id === selectedListId);

  return (
    <div className="CopyTaskModal-backdrop" onClick={onClose}>
      <div className="CopyTaskModal" onClick={(e) => e.stopPropagation()}>
        <div className="CopyTaskModal-header">
          <h2>Copy Task</h2>
          <button className="CopyTaskModal-close" onClick={onClose}>✕</button>
        </div>

        <div className="CopyTaskModal-body">
          {/* Task info */}
          <div className="CopyTaskModal-taskInfo">
            <p><strong>Copying:</strong> {task.title}</p>
          </div>

          {/* Workspace selector */}
          <div className="CopyTaskModal-field">
            <label>Target Workspace:</label>
            <select
              value={selectedWorkspaceId}
              onChange={(e) => setSelectedWorkspaceId(e.target.value)}
              className="CopyTaskModal-select"
            >
              <option value="">Select workspace...</option>
              {workspaces.map(workspace => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          </div>

          {/* List selector */}
          <div className="CopyTaskModal-field">
            <label>Target List:</label>
            <select
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              className="CopyTaskModal-select"
              disabled={!selectedWorkspaceId || loadingLists}
            >
              {loadingLists ? (
                <option value="">Loading lists...</option>
              ) : (
                <>
                  <option value="">Select list...</option>
                  {availableLists.map(list => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </>
              )}
            </select>
            {selectedWorkspaceId && !loadingLists && availableLists.length === 0 && (
              <p className="CopyTaskModal-warning">No lists in this workspace</p>
            )}
          </div>

          {/* Copy options */}
          <div className="CopyTaskModal-section">
            <h3>What to include:</h3>

            <label className="CopyTaskModal-checkbox">
              <input
                type="checkbox"
                checked={includeLabels}
                onChange={(e) => setIncludeLabels(e.target.checked)}
              />
              <span>Labels</span>
              <small className="CopyTaskModal-hint">
                Matching labels will be created if they don't exist in target workspace
              </small>
            </label>

            <label className="CopyTaskModal-checkbox">
              <input
                type="checkbox"
                checked={includeComments}
                onChange={(e) => setIncludeComments(e.target.checked)}
              />
              <span>Comments ({task.comment_count || 0})</span>
              <small className="CopyTaskModal-hint">
                All comments will be copied with original timestamps
              </small>
            </label>

            <label className="CopyTaskModal-checkbox">
              <input
                type="checkbox"
                checked={includeAttachments}
                onChange={(e) => setIncludeAttachments(e.target.checked)}
                disabled
              />
              <span>Attachments (Coming soon)</span>
              <small className="CopyTaskModal-hint">
                File copying not yet implemented
              </small>
            </label>

            <label className="CopyTaskModal-checkbox">
              <input
                type="checkbox"
                checked={includeMediaPanel}
                onChange={(e) => setIncludeMediaPanel(e.target.checked)}
              />
              <span>Media Panel</span>
              <small className="CopyTaskModal-hint">
                Rich content, videos, and images
              </small>
            </label>
          </div>

          {/* Warnings */}
          <div className="CopyTaskModal-warnings">
            <p><strong>Note:</strong></p>
            <ul>
              <li>Task will be copied to the bottom of the target list</li>
              <li>Status will be reset to "To Do"</li>
              <li>Assigned user will not be copied (may not have access to target workspace)</li>
              <li>Title will be appended with " (copy)"</li>
            </ul>
          </div>

          {error && (
            <div className="CopyTaskModal-error">
              {error}
            </div>
          )}
        </div>

        <div className="CopyTaskModal-footer">
          <button
            className="CopyTaskModal-btn secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="CopyTaskModal-btn primary"
            onClick={handleCopy}
            disabled={isLoading || !selectedListId}
          >
            {isLoading ? 'Copying...' : 'Copy Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
